/**
 * Utility functions for forwarders functionality
 */

/**
 * Maps an array of objects with id property to an object with id as key
 * @param {Array} names - Array of objects with id property
 * @returns {Object} Object with id as key and original object as value
 */
export const nameMap = (names) =>
  names.reduce((obj, item) => {
    obj[item.id] = item
    return obj
  }, {})

/**
 * Normalizes forwarder payload for API submission
 * @param {Object} item - Forwarder item to normalize
 * @returns {Object} Normalized payload
 */
export const normalizeForwarderPayload = (item) => {
  const payload = JSON.parse(JSON.stringify(item))
  
  if (payload.system_log) {
    payload.system_log.log_send_interval_seconds = parseInt(payload.system_log.log_send_interval_seconds)
    payload.system_log.batch_size = parseInt(payload.system_log.batch_size)
  }
  
  if (payload.resource_monitor) {
    payload.resource_monitor.check_interval_seconds = parseInt(payload.resource_monitor.check_interval_seconds)
    payload.resource_monitor.log_send_interval_seconds = parseInt(payload.resource_monitor.log_send_interval_seconds)
    payload.resource_monitor.batch_size = parseInt(payload.resource_monitor.batch_size)
  }
  
  if (payload.file_log) {
    payload.file_log.log_send_interval_seconds = parseInt(payload.file_log.log_send_interval_seconds)
    payload.file_log.batch_size = parseInt(payload.file_log.batch_size)
  }
  
  return payload
}

/**
 * Creates a default forwarder item structure
 * @returns {Object} Default forwarder item
 */
export const createDefaultForwarderItem = () => ({
  enabled: true,
  system_log: {
    enabled: false,
    log_send_interval_seconds: 600,
    batch_size: 500,
    windows: { enabled: false, index_id: '', source_type_id: '', channels: [], custom_paths: [] },
    linux: { enabled: false, index_id: '', source_type_id: '', channels: [], custom_paths: [] }
  },
  resource_monitor: {
    enabled: false,
    index_id: '',
    source_type_id: '',
    check_interval_seconds: 60,
    log_send_interval_seconds: 600,
    batch_size: 500,
    metrics: []
  },
  file_log: {
    enabled: false,
    log_send_interval_seconds: 15,
    batch_size: 100,
    windows: { enabled: false, streams: [] },
    linux: { enabled: false, streams: [] }
  }
})

/**
 * Ensures forwarder item has complete structure
 * @param {Object} item - Forwarder item to normalize
 * @returns {Object} Item with complete structure
 */
export const ensureForwarderStructure = (item) => {
  const normalizedItem = JSON.parse(JSON.stringify(item))
  
  if (!normalizedItem.system_log) {
    normalizedItem.system_log = {
      enabled: false,
      log_send_interval_seconds: 600,
      batch_size: 500,
      windows: { enabled: false, index_id: '', source_type_id: '', channels: [], custom_paths: [] },
      linux: { enabled: false, index_id: '', source_type_id: '', channels: [], custom_paths: [] }
    }
  }
  
  if (!normalizedItem.resource_monitor) {
    normalizedItem.resource_monitor = {
      enabled: false,
      index_id: '',
      source_type_id: '',
      check_interval_seconds: 60,
      log_send_interval_seconds: 600,
      batch_size: 500,
      metrics: []
    }
  }
  
  if (!normalizedItem.file_log) {
    normalizedItem.file_log = {
      enabled: false,
      log_send_interval_seconds: 15,
      batch_size: 100,
      windows: { enabled: false, streams: [] },
      linux: { enabled: false, streams: [] }
    }
  } else {
    // Ensure file_log has complete structure even if it exists
    if (!normalizedItem.file_log.windows) {
      normalizedItem.file_log.windows = { enabled: false, streams: [] }
    } else if (!normalizedItem.file_log.windows.streams) {
      normalizedItem.file_log.windows.streams = []
    }
    
    if (!normalizedItem.file_log.linux) {
      normalizedItem.file_log.linux = { enabled: false, streams: [] }
    } else if (!normalizedItem.file_log.linux.streams) {
      normalizedItem.file_log.linux.streams = []
    }
    
    // Ensure required numeric fields have default values
    if (typeof normalizedItem.file_log.log_send_interval_seconds === 'undefined') {
      normalizedItem.file_log.log_send_interval_seconds = 15
    }
    if (typeof normalizedItem.file_log.batch_size === 'undefined') {
      normalizedItem.file_log.batch_size = 100
    }
  }
  
  return normalizedItem
}

/**
 * File upload configuration for different OS types
 */
export const FILE_UPLOAD_CONFIG = {
  fileInfo: {
    'windows_x64': { os: 'windows', architecture: 'x64' },
    'windows_x32': { os: 'windows', architecture: 'x32' },
    'linux_x64': { os: 'linux', architecture: 'x64' },
    'linux_x32': { os: 'linux', architecture: 'x32' }
  },
  fileTypes: ['windows_x64', 'windows_x32', 'linux_x64', 'linux_x32'],
  fileFieldMap: {
    'windows_x64': 'windows_x64',
    'windows_x32': 'windows_x86',
    'linux_x64': 'linux_x64',
    'linux_x32': 'linux_x86'
  }
}

/**
 * Creates initial drag states for file upload
 * @returns {Object} Initial drag states
 */
export const createInitialDragStates = () => ({
  windows_x64: false,
  windows_x32: false,
  linux_x64: false,
  linux_x32: false
})
